import { useState } from "react";
import {
    Box,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
    ApiMessageHandler,
    ArtButton,
    ArtDialog,
    useApiService,
    useIdpService,
} from "@art/shared-ui";
import { useIDPContext } from '../../../auth';


const passwordRules = [
    { label: "At least 8 characters", test: (value: string) => value.length >= 8 },
    { label: "One uppercase letter", test: (value: string) => /[A-Z]/.test(value) },
    { label: "One lowercase letter", test: (value: string) => /[a-z]/.test(value) },
    { label: "One number", test: (value: string) => /[0-9]/.test(value) },
    { label: "One special character", test: (value: string) => /[^A-Za-z0-9]/.test(value) },
];

type PasswordFieldName = "current" | "next" | "confirm";

const emptyForm: Record<PasswordFieldName, string> = {
    current: "",
    next: "",
    confirm: "",
};

const allHidden: Record<PasswordFieldName, boolean> = {
    current: false,
    next: false,
    confirm: false,
};

const emptyErrors: Record<PasswordFieldName, string> = {
    current: "",
    next: "",
    confirm: "",
};


const STRENGTH_MESSAGE =
    "Use 8+ characters with upper, lower, number and special character";

const validateForm = (form: Record<PasswordFieldName, string>) => {
    const errors = { ...emptyErrors };

    if (!form.current) {
        errors.current = "Current password is required";
    }

    if (!form.next) {
        errors.next = "New password is required";
    } else if (!passwordRules.every((rule) => rule.test(form.next))) {
        errors.next = STRENGTH_MESSAGE;
    } else if (form.next === form.current) {
        errors.next = "Choose a password different from your current one";
    }

    if (!form.confirm) {
        errors.confirm = "Please confirm your new password";
    } else if (form.confirm !== form.next) {
        errors.confirm = "Passwords do not match";
    }

    return errors;
};

interface PasswordFieldProps {
    label: string;
    value: string;
    visible: boolean;
    onToggle: () => void;
    onChange: (value: string) => void;
    error?: string;
    autoFocus?: boolean;
    autoComplete: "current-password" | "new-password";
    dataQa: string;
    marginBottom?: number;
}

const PasswordField = ({
    label,
    value,
    visible,
    onToggle,
    onChange,
    error,
    autoFocus,
    autoComplete,
    dataQa,
    marginBottom,
}: PasswordFieldProps) => (
    <TextField
        fullWidth
        size="small"
        type={visible ? "text" : "password"}
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        error={!!error}
        helperText={error}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        sx={{

            marginBottom: `${marginBottom ?? 30}px`,
            "& input:-webkit-autofill": {
                transition: "background-color 600000s 0s, color 600000s 0s",
            },
        }}
        slotProps={{
            htmlInput: { "data-qa": dataQa },
            input: {
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            size="small"
                            edge="end"
                            tabIndex={-1}
                            onClick={onToggle}
                            aria-label={visible ? `Hide ${label}` : `Show ${label}`}
                            sx={{
                                color: "text.secondary",
                                "&:hover": { color: "var(--art-primary, #04adc3)" },
                            }}
                        >
                            {visible ? (
                                <VisibilityOff fontSize="small" />
                            ) : (
                                <Visibility fontSize="small" />
                            )}
                        </IconButton>
                    </InputAdornment>
                ),
            },
        }}
    />
);

interface ChangePasswordFieldsProps {
    email?: string;
    form: Record<PasswordFieldName, string>;
    visible: Record<PasswordFieldName, boolean>;
    errors: Record<PasswordFieldName, string>;
    onFieldChange: (field: PasswordFieldName, value: string) => void;
    onToggleVisible: (field: PasswordFieldName) => void;
}

const ChangePasswordFields = ({
    email,
    form,
    visible,
    errors,
    onFieldChange,
    onToggleVisible,
}: ChangePasswordFieldsProps) => (
    <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Updating the password for <strong>{email}</strong>.
        </Typography>

        <PasswordField
            label="Current password"
            dataQa="current-password"
            autoComplete="current-password"
            autoFocus
            value={form.current}
            visible={visible.current}
            onToggle={() => onToggleVisible("current")}
            onChange={(value) => onFieldChange("current", value)}
            error={errors.current}
        />

        <PasswordField
            label="New password"
            dataQa="new-password"
            autoComplete="new-password"
            value={form.next}
            visible={visible.next}
            onToggle={() => onToggleVisible("next")}
            onChange={(value) => onFieldChange("next", value)}
            error={errors.next}
        />

        <PasswordField
            label="Confirm new password"
            dataQa="confirm-password"
            autoComplete="new-password"
            value={form.confirm}
            visible={visible.confirm}
            onToggle={() => onToggleVisible("confirm")}
            onChange={(value) => onFieldChange("confirm", value)}
            error={errors.confirm}
            // Last field: the dialog's own bottom padding leaves room for the helper.
            marginBottom={0}
        />
    </>
);

interface ChangePasswordDialogProps {
    open: boolean;
    onClose: () => void;
}

const ChangePasswordDialog = ({ open, onClose }: ChangePasswordDialogProps) => {
    const apiService = useApiService();
    const idpService = useIdpService();
    const { authGuardInstance } = useIDPContext() as { authGuardInstance: any };

    const [form, setForm] = useState(emptyForm);
    const [visible, setVisible] = useState(allHidden);
    const [errors, setErrors] = useState(emptyErrors);
    const [isSaving, setIsSaving] = useState(false);

    const handleClose = () => {
        setForm(emptyForm);
        setVisible(allHidden);
        setErrors(emptyErrors);
        onClose();
    };

    const updateField = (field: PasswordFieldName, value: string) => {
        setForm((previous) => ({ ...previous, [field]: value }));
     
        setErrors((previous) =>
            previous[field] ? { ...previous, [field]: "" } : previous
        );
    };

    const toggleVisible = (field: PasswordFieldName) => {
        setVisible((previous) => ({ ...previous, [field]: !previous[field] }));
    };

    const handleSubmit = async () => {
        if (isSaving) {
            return;
        }

        const validationErrors = validateForm(form);
        setErrors(validationErrors);
        if (Object.values(validationErrors).some(Boolean)) {
            return;
        }

        try {
            setIsSaving(true);
            await authGuardInstance.ensureFreshToken();
            const path = `api/tenants/${authGuardInstance.tenantName}/users/profile/change-password`;
            const payload = {
                current_password: form.current,
                password: form.next,
            };
            let res;
            try {
                res = await idpService.put(path, payload, { showErrorSnack: false });
            } catch (error: any) {
                // This response is emitted by JWT middleware before the password handler runs.
                // A delayed browser tab can miss its scheduled refresh; retry once with a new token.
                if (error?.response?.status !== 401 || error?.response?.data?.error_code !== "AUTH_TOKEN_INVALID") {
                    throw error;
                }
                await authGuardInstance.refreshToken();
                if (!authGuardInstance.token) throw new Error("Session expired. Please sign in again.");
                res = await idpService.put(path, payload, { showErrorSnack: false });
            }
            apiService.showSnack(
                res.data?.message || "Password updated successfully",
                "success"
            );
            handleClose();
        } catch (error: any) {
            const message = ApiMessageHandler.extractErrorMessage(error);

  
            const code = error?.response?.data?.code;
            if (error?.response?.status === 400 && code === "000031") {
                setErrors((previous) => ({ ...previous, next: message }));
            } else if (error?.response?.status === 400) {
                setErrors((previous) => ({ ...previous, current: message }));
            } else {
                apiService.showSnack(message, "error");
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <ArtDialog
            open={open}
            onClose={handleClose}
            title="Change Password"
            size="medium"
            dataQa="dialog-change-password"
            actions={
                <Box sx={{ display: "flex", gap: "15px" }}>
                    <ArtButton
                        label={isSaving ? "Updating..." : "Update Password"}
                        buttonType="button"
                        dataQa="btn-update-password"
                        disabled={isSaving}
                        onClick={handleSubmit}
                    />
                    <ArtButton
                        label="Cancel"
                        variant="outlined"
                        color="error"
                        buttonType="button"
                        dataQa="btn-cancel"
                        disabled={isSaving}
                        onClick={handleClose}
                    />
                </Box>
            }
        >
            <ChangePasswordFields
                email={authGuardInstance?.user?.email}
                form={form}
                visible={visible}
                errors={errors}
                onFieldChange={updateField}
                onToggleVisible={toggleVisible}
            />
        </ArtDialog>
    );
};

export default ChangePasswordDialog;
export { ChangePasswordDialog };
